// Language layer. The UI is written in English; every visible string goes through t(),
// whose argument is the English text and the key of the Thai dictionary (th.ts).
// Placeholders: t("{n} rows", { n }). test/i18n.test.ts checks that every string
// passed to t() / tn() has a Thai entry. GPL-3.0-or-later.
import { TH } from "./th.js";

export type Lang = "en" | "th";
const KEY = "dfsuite.lang";
export let lang: Lang = "en";
try { const v = localStorage.getItem(KEY); if (v === "th" || v === "en") lang = v; } catch { /* default */ }

type Vars = Record<string, string | number>;
const fill = (s: string, vars?: Vars) => {
  if (!vars) return s;
  for (const k in vars) s = s.split("{" + k + "}").join(String(vars[k]));
  return s;
};
/** Add the Thai of a module loaded on demand (its dictionary travels with its chunk). */
export function extendTH(d: Record<string, string>) { Object.assign(TH, d); }
/** The string in the current language. Unknown keys fall back to English. */
export function t(en: string, vars?: Vars): string {
  return fill(lang === "th" ? TH[en] ?? en : en, vars);
}
/** Run `f` with every string in English, whatever the UI language (the report, D-024).
 *  Synchronous: the language is the UI's again as soon as `f` returns. */
export function inEnglish<T>(f: () => T): T {
  const was = lang; lang = "en";
  try { return f(); } finally { lang = was; }
}
/** Singular / plural by count; `{n}` is the count. */
export function tn(n: number, one: string, other: string, vars?: Vars): string {
  return t(n === 1 ? one : other, { n: n.toLocaleString("en-US"), ...vars });
}

const listeners = new Set<() => void>();
export function setLang(l: Lang) {
  if (l === lang) return;
  lang = l; document.documentElement.lang = l;
  try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
  listeners.forEach((f) => f());
}
export const onLang = (f: () => void) => { listeners.add(f); return () => listeners.delete(f); };
export const dateLocale = () => (lang === "th" ? "th-TH" : "en-GB");
if (typeof document !== "undefined") document.documentElement.lang = lang;
